import { ComponentInterface, Entity, Runner } from '@goodboydigital/odie';
import { Graphics } from '@pixi/graphics';
import p2 from 'p2';

import { Maths } from '../../utils/Maths';
import { BaseEntity } from '../entities/BaseEntity';
import { Game } from '../Game';
import { PhysicsSystem } from './PhysicsSystem';
import { PhysicsPart } from './PhysicsUtils';

export class PhysicsComponent implements ComponentInterface
{
    public entity!: BaseEntity;
    public bodies: p2.Body[];
    public springs: p2.Spring[];
    public constraints: p2.Constraint[];
    public copyRotationTo?: p2.Body;
    public composed = false;
    public onBeginContact: Runner;
    public onEndContact: Runner;
    public physicsFixedUpdateRunner: Runner;
    public physicsFreeUpdateRunner: Runner;
    public physicsPostStepRunner: Runner;
    public constantRotation = 0;
    public entityOffsetX = 0;
    public entityOffsetY = 0;
    public rotationOffset = 0;
    public collisionActive: PhysicsComponent | null = null;
    public view?: Graphics;
    public debug = false;
    public mainBodyIndex = 0;
    public harmPlayer = false;
    public system?: PhysicsSystem;
    public viewsToRotate?: {rotation: number}[];
    public useRotationRate = false;
    public culling = false;
    public ground = false;

    constructor()
    {
        this.bodies = [];
        this.springs = [];
        this.constraints = [];
        this.physicsFixedUpdateRunner = new Runner('physicsFixedUpdate', 1);
        this.physicsFreeUpdateRunner = new Runner('physicsFreeUpdate', 1);
        this.physicsPostStepRunner = new Runner('physicsPostStep', 1);
        this.onBeginContact = new Runner('onBeginContact', 1);
        this.onEndContact = new Runner('onEndContact', 1);
    }

    public addedToEntity(entity: Entity): void
    {
        this.entity = entity as BaseEntity;
        this.onBeginContact.add(this.entity as any);
        this.onEndContact.add(this.entity as any);
    }

    // === PARTS (BODY/CONSTRAINT/SPRING) =============================================================================

    /**
     * A single entity can have multiple bodies/constraints/springs, all managed by
     * this single component. By default, the first body added is considered the main body,
     * and that body will define entity position and rotation.
     * @param parts - Physics parts managed by this entity
     */
    public addParts(...parts: PhysicsPart[]): void
    {
        const world = (this.entity?.scene as Game)?.physics.world;

        for (const part of parts)
        {
            if (part instanceof p2.Body)
            {
                part.physicsComponent = this;
                this.bodies.push(part);
                if (world) world.addBody(part);
            }
            else if (part instanceof p2.Constraint)
            {
                this.constraints.push(part);
                if (world) world.addConstraint(part);
            }
            else if (part instanceof p2.Spring)
            {
                this.springs.push(part);
                if (world) world.addSpring(part);
            }
        }
    }

    /**
     * Remove all physics bodies/constraints/springs managed by this component
     */
    public clearParts(): void
    {
        for (const part of this.constraints)
        {
            if (part.bodyA.world) part.bodyA.world.removeConstraint(part);
        }
        this.constraints = [];

        for (const part of this.springs)
        {
            if (part.bodyA.world) part.bodyA.world.removeSpring(part);
        }
        this.springs = [];

        for (const part of this.bodies)
        {
            if (part.world) part.world.removeBody(part);
            part.onBeginContact = undefined;
            part.onEndContact = undefined;
            part.physicsComponent = undefined;
        }

        this.bodies = [];
        this.viewsToRotate = undefined;
    }

    /** Get the main body of this component. Can be undefined. */
    public get mainBody(): p2.Body
    {
        return this.bodies[this.mainBodyIndex];
    }

    // === TRANSFORM ==================================================================================================

    public get x(): number
    {
        return this.mainBody.position[0];
    }

    public set x(v: number)
    {
        this.mainBody.position[0] = v;
    }

    public get y(): number
    {
        return -this.mainBody.position[1];
    }

    public set y(v: number)
    {
        this.mainBody.position[1] = -v;
    }

    public get angle(): number
    {
        return this.mainBody.angle;
    }

    public setPosition(x: number, y: number): void
    {
        const px = x;
        const py = -y;
        const vx = px - (this.mainBody?.position[0] || 0);
        const vy = py - (this.mainBody?.position[1] || 0);

        for (const body of this.bodies)
        {
            body.position[0] += vx;
            body.position[1] += vy;
        }
    }

    public setAngle(angle: number, x?: number, y?: number): void
    {
        angle *= -1;
        if (!x) x = this.mainBody?.position[0] || 0;
        if (!y) y = this.mainBody?.position[1] || 0;

        for (const body of this.bodies)
        {
            const dist = Maths.distance(x, y, body.position[0], body.position[1]);
            const a = Math.atan2(y - body.position[1], x - body.position[0]);

            body.position[0] = x - (Math.cos(a + angle) * dist);
            body.position[1] = y - (Math.sin(a + angle) * dist);
            body.angle = angle;
        }
    }

    public setTransform(x = 0, y = 0, angle = 0, clearForces = true): void
    {
        if (clearForces) this.clearForces();
        this.setPosition(x, y);
        this.setAngle(angle);
    }

    public translate(vx: number, vy: number): void
    {
        for (const body of this.bodies)
        {
            body.position[0] += vx;
            body.position[1] += vy;
        }
    }

    public rotate(va: number): void
    {
        if (va === 0) return;
        this.setAngle(this.mainBody.angle + va);
    }

    // UTILITY ========================================================================================================

    public breakConstraints(): void
    {
        if (!this.system) return;
        this.system.safeRemoveParts(...this.constraints);
        this.system.safeRemoveParts(...this.springs);
    }

    public relinkConstraints(): void
    {
        if (!this.system) return;
        this.system.safeAddParts(...this.constraints);
        this.system.safeAddParts(...this.springs);
    }

    public clearForces(): void
    {
        for (const body of this.bodies)
        {
            body.setZeroForce();
            body.velocity[0] = 0;
            body.velocity[1] = 0;
            body.angularVelocity = 0;
        }
    }

    public applyImpulseRelativeToMass(x: number, y: number, clearForces = false): void
    {
        for (const body of this.bodies)
        {
            if (clearForces) this.clearForces();
            body.applyImpulse([x * body.mass, y * body.mass]);
        }
    }

    // UPDATE =========================================================================================================

    public physicsFixedUpdate(step = 1): void
    {
        this.physicsFixedUpdateRunner.dispatch(step);
    }

    public physicsFreeUpdate(step = 1): void
    {
        if (!this.entity) return;

        if (this.mainBody)
        {
            const main = this.mainBody;

            // Interpolated not working for static bodies??
            // this.entity.x = main.interpolatedPosition[0];
            // this.entity.y = -main.interpolatedPosition[1];
            // this.entity.transform.rotation = -main.interpolatedAngle;
            this.entity.x = main.position[0];
            this.entity.y = -main.position[1];

            if (this.viewsToRotate)
            {
                let i = this.viewsToRotate.length;

                while (i--)
                {
                    const view = this.viewsToRotate[i];

                    // Cheap solution for rotating sprites in different rates
                    if (this.useRotationRate)
                    {
                        const rate = (view as any).rotationRate ?? 1;

                        view.rotation = (-main.angle + this.rotationOffset) * rate;
                    }
                    else
                    {
                        view.rotation = -main.angle + this.rotationOffset;
                    }
                }
            }
            else
            {
                this.entity.transform.rotation = -main.angle + this.rotationOffset;
            }
        }

        this.physicsFreeUpdateRunner.dispatch(step);
        if (this.culling && this.system?.useCulling) this.cullingUpdate();
    }

    public ensureEntityPosition(): void
    {
        if (!this.entity || !this.mainBody) return;
        this.entity.x = this.mainBody.position[0];
        this.entity.y = -this.mainBody.position[1];
        this.entity.view.visible = true;
        this.entity.view.renderable = true;
        this.entity.view25d.render();
        this.entity.view.updateTransform();
    }

    // CULLING
    private cullingUpdate(): void
    {
        if (!this.system || !this.system.game.player) return;
        const world = this.system?.world;
        const chassis = this.system.game.player.vehicle.chassis;
        const aLeft = chassis.physics.mainBody.position[0] - 256;
        const aRight = chassis.physics.mainBody.position[0] + 256;
        const aTop = chassis.physics.mainBody.position[1] + 256;
        const aBottom = chassis.physics.mainBody.position[1] - 256;
        let i = this.bodies.length;

        while (i--)
        {
            const body = this.bodies[i];
            const bLeft = body.position[0] - body.boundingRadius;
            const bRight = body.position[0] + body.boundingRadius;
            const bTop = body.position[1] + body.boundingRadius;
            const bBottom = body.position[1] - body.boundingRadius;
            const inRangeH = bRight > aLeft && bLeft < aRight;
            const inRangeV = bBottom < aTop && bTop > aBottom;
            const inRange = inRangeH && inRangeV;

            if (inRange && !body.world)
            {
                world.addBody(body);
            }
            else if (!inRange && body.world)
            {
                world.removeBody(body);
            }
        }
    }
}
